import React, { useEffect, useState } from "react";
import { styled } from "../stitches.config";
import Footer from "../redesign/components/Footer";
import PhotoOnLeft from "../redesign/components/PhotoOnLeft";
import PhotoOnLeftBig from "../redesign/components/PhotoOnLeftBig";
import PageCover from "../redesign/components/PageCover";
import Breadcrumbs from "../redesign/components/Breadcrumbs";
import Button from '../redesign/atoms/Button';

interface Picture {
  img: string;
}

interface Article {
  OID: number;
  title: string;
  lead: string;
  date: string;
  slug: string;
  url: string;
  picture?: Picture;
}

interface ApiResponse {
  currentPage: number;
  pages: number;
  posts: Article[];
}

const parseDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatPL = (date: Date) => {
  return date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatURL = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const ArchivesPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dateArchive, setDateArchive] = useState<string | null>(null);
  const isDesktop = (window.APP_SETTINGS.device === 'desktop');
  const domain = window.APP_SETTINGS.domain;

  useEffect(() => {
    const path = window.location.pathname;
    const parts = path.split("/");
    setDateArchive(parts[2] || null);
  }, []);

  useEffect(() => {
    if (!dateArchive) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `https://${domain}/api/posts?pageSize=1000&date=${dateArchive}`
        );

        const data: ApiResponse = await res.json();
        setArticles(data.posts || []);
      } catch (err) {
        console.error("Błąd fetchowania:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateArchive]);

  const currentDate = dateArchive ? parseDate(dateArchive) : null;

  const prevDate = currentDate
    ? new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() - 1
    )
    : null;

  const nextDate = currentDate
    ? new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1
    )
    : null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const showNext =
    nextDate && nextDate.getTime() < today.getTime();

  const skeletonCount = articles.length > 0 ? articles.length : 5;
  return (
    <>
      <Wrapper>
        <PageCover
          beforeContent={
            <Breadcrumbs
              category={{ name: "Newsroom", link: "/newsroom" }}
              subCategory={{
                name: dateArchive || "",
                link: `/newsroom/${dateArchive}`,
              }}
              allWhite
            />
          }
          title={`Artykuły z  ${currentDate ? formatPL(currentDate) : ""}`}
          description={""}
          isDescriptionWyswig={false}
        />

        <div className="container">
          <List>
            {loading &&
              Array.from({ length: skeletonCount }).map((_, index) => (
                <React.Fragment key={`skeleton-${index}`}>
                  {isDesktop ? (
                    <PhotoOnLeftBig showDate article={{} as any} image="" />
                  ) : (
                    <PhotoOnLeft article={{} as any} />
                  )}
                  {index !== skeletonCount - 1 && <ListSeparator />}
                </React.Fragment>
              ))}

            {!loading &&
              articles.map((article, index) => (
                <React.Fragment key={article.OID}>
                  {isDesktop ?
                    <PhotoOnLeftBig
                      showDate={true}
                      article={article as any}
                      image={""}
                    />
                    :
                    <PhotoOnLeft
                      boxSize="xs" article={article}
                    />
                  }
                  {index !== articles.length - 1 && <ListSeparator />}
                </React.Fragment>
              ))}
          </List>

          {currentDate && (
            <Nav>
              {prevDate && (
                <a
                  href={`/newsroom/${formatURL(prevDate)}`}
                  rel="prev"
                >
                  <Button
                    text={`< ${formatPL(prevDate)}`}
                    type={'secondary'}
                    starPosition="none"
                  />
                </a>
              )}

              {showNext && nextDate && (
                <>
                  <a
                    href={`/newsroom/${formatURL(nextDate)}`}
                    rel="next"
                  >
                    <Button
                      text={`${formatPL(nextDate)} >`}
                      type={'secondary'}
                      starPosition="none"
                    />
                  </a>
                </>
              )}
            </Nav>
          )}
        </div>
      </Wrapper>

      <Footer />
    </>
  );
};

const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const List = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  marginTop: "24px",

  "@bp4": {
    marginTop: "64px",
  },

  ".photo-on-left": {
    marginBottom: "0px",
  }
});

const ListSeparator = styled("span", {
  display: "block",
  width: "100%",
  height: "1px",
  backgroundColor: "#E5E5E5",
  margin: "24px 0",
  "@bp4": {
    margin: "40px 0",
  },
});

const Nav = styled("div", {
  marginTop: "40px",
  display: "flex",
  justifyContent: "center",
  gap: "12px",
  fontSize: "16px",

  a: {
    textDecoration: "none",
    color: "black",

    "&:hover": {
      textDecoration: "underline",
    },
  },

  ".separator": {
    opacity: 0.5,
  },
});

export default ArchivesPage;

